import React, { useEffect, useRef, useState, useCallback } from 'react';
import './WebGLContainer.css';

// Global flag to prevent multiple Unity instances
let isUnityLoading = false;

// Screen size detection and performance optimization
const detectScreenSizeAndPerformance = () => {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const diagonal = Math.sqrt(width * width + height * height);
  
  let newScreenSize = 'medium';
  let newPerformanceMode = false;
  let force1920x1080 = false;
  let scaleToFit = false;
  let scaleUnity = false;
  let scaleMobile = false;
  
  // Check if device is mobile (portrait orientation, but only if width > 768px)
  const isMobile = height > width && width > 768;
  
  console.log(`Screen detection: ${width}x${height}, isMobile: ${isMobile}, height > width: ${height > width}, width > 768: ${width > 768}`);
  
  // Scale Unity for screens wider than 1700px (performance-friendly zoom)
  if (width > 1700) {
    newScreenSize = 'ultra-large';
    newPerformanceMode = true;
    scaleUnity = true;
    console.log('Ultra-large screen detected (width > 1700px)');
  } else if (isMobile) {
    // For mobile devices (portrait orientation with width > 768px), use width-based scaling with 16:9 aspect ratio
    newScreenSize = 'small';
    scaleMobile = true;
    console.log('Mobile device detected - using width-based scaling');
  } else {
    // For desktop screens and small screens (width <= 768px), scale to fit using smallest dimension
    scaleToFit = true;
    if (width <= 768) {
      newScreenSize = 'small';
    } else if (width <= 1200) {
      newScreenSize = 'medium';
    } else if (width <= 1600) {
      newScreenSize = 'large';
    } else if (width <= 1700) {
      newScreenSize = 'ultra-large';
    }
    console.log('Desktop/small screen device detected - using scale-to-fit');
  }
  
  // Enable performance mode for high-resolution screens
  if (diagonal > 2000) {
    newPerformanceMode = true;
  }
  
  return { screenSize: newScreenSize, performanceMode: newPerformanceMode, force1920x1080, scaleToFit, scaleUnity, scaleMobile };
};

const WebGLContainer = () => {
  const containerRef = useRef(null);
  const unityInstanceRef = useRef(null);
  const isUnmountingRef = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [compressionError, setCompressionError] = useState(false);
  const [screenSize, setScreenSize] = useState('medium');
  const [performanceMode, setPerformanceMode] = useState(false);
  const [force1920x1080, setForce1920x1080] = useState(false);
  const [scaleToFit, setScaleToFit] = useState(false);
  const [scaleUnity, setScaleUnity] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [scaleMobile, setScaleMobile] = useState(false);

  const cleanupUnityInstance = useCallback(() => {
    // Don't cleanup if component is unmounting
    if (isUnmountingRef.current) {
      return;
    }
    
    // Clean up existing Unity instance
    if (unityInstanceRef.current) {
      try {
        console.log('Cleaning up existing Unity instance...');
        unityInstanceRef.current.Quit();
        unityInstanceRef.current = null;
      } catch (err) {
        console.warn('Error cleaning up Unity instance:', err);
      }
    }
    
    // Remove existing Unity container safely
    const existingContainer = document.getElementById('unity-container');
    if (existingContainer && existingContainer.parentNode) {
      try {
        existingContainer.remove();
      } catch (err) {
        console.warn('Error removing Unity container:', err);
      }
    }
    
    // Clear any existing Unity scripts safely
    const existingScripts = document.querySelectorAll('script[src*="game.loader.js"], script[src*="socket.io"]');
    existingScripts.forEach(script => {
      if (script && script.parentNode) {
        try {
          script.parentNode.removeChild(script);
        } catch (err) {
          console.warn('Error removing script:', err);
        }
      }
    });
    
    // Clear global Unity instance
    if (window.unityInstance) {
      window.unityInstance = null;
    }
  }, []);

  const loadUnityWebGL = useCallback(() => {
    try {
      // Check if Unity is already loading globally
      if (isUnityLoading) {
        console.log('Unity is already loading, skipping initialization');
        return;
      }
      
      // Check if Unity is already loaded
      if (unityInstanceRef.current || document.getElementById('unity-container')) {
        console.log('Unity instance already exists, skipping initialization');
        return;
      }
      
      // Set global loading flag
      isUnityLoading = true;
      
      // Clean up any existing Unity instance first
      cleanupUnityInstance();
      
      setIsLoading(true);
      setError(null);
      setCompressionError(false);

      // Create Unity WebGL container
      const unityContainer = document.createElement('div');
      unityContainer.id = 'unity-container';
      unityContainer.className = 'unity-container';

      // Create Unity canvas - let Unity handle the size
      const unityCanvas = document.createElement('canvas');
      unityCanvas.id = 'unity-canvas';
      unityCanvas.className = 'unity-canvas';
      
      // Don't set canvas.width/height - let Unity handle it
      // Just set CSS limits to prevent performance issues
      const maxWidth = 1700;
      const maxHeight = 956;
      
      unityCanvas.style.maxWidth = `${maxWidth}px`;
      unityCanvas.style.maxHeight = `${maxHeight}px`;
      unityCanvas.style.width = '100%';
      unityCanvas.style.height = '100%';
      
      console.log(`Canvas limits set to: ${maxWidth}x${maxHeight}, Unity handles actual size`);
      
      unityContainer.appendChild(unityCanvas);

      // Create Unity loading bar
      const loadingBar = document.createElement('div');
      loadingBar.id = 'unity-loading-bar';
      loadingBar.className = 'unity-loading-bar';
      
      const loadingBarFill = document.createElement('div');
      loadingBarFill.id = 'unity-loading-bar-fill';
      loadingBarFill.className = 'unity-loading-bar-fill';
      loadingBar.appendChild(loadingBarFill);
      
      unityContainer.appendChild(loadingBar);

      // Add to container
      if (containerRef.current) {
        containerRef.current.appendChild(unityContainer);
      }

      // Load Unity WebGL
      loadUnityBuild();

    } catch (err) {
      setError('Failed to initialize Unity WebGL: ' + err.message);
      setIsLoading(false);
    }
  }, [cleanupUnityInstance]);

  const loadUnityBuild = useCallback(() => {
    // Check if we're on HTTPS or localhost
    const isSecure = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
    
    if (!isSecure) {
      setCompressionError(true);
      setError('HTTPS Required: Unity WebGL requires HTTPS for Brotli compression. Please use HTTPS or localhost.');
      setIsLoading(false);
      return;
    }

    // Add console logging for debugging
    console.log('Loading Unity WebGL build...');
    console.log('Current protocol:', window.location.protocol);
    console.log('Current hostname:', window.location.hostname);
    console.log('Is secure context:', isSecure);

    // Load Socket.IO client library first
    const socketIOScript = document.createElement('script');
    socketIOScript.src = 'https://cdn.socket.io/4.7.2/socket.io.min.js';
    socketIOScript.onload = () => {
      console.log('Socket.IO client library loaded successfully');
      
        // Unity WebGL loader script (newer format)
        const script = document.createElement('script');
        script.src = '/game/Build/game.loader.js?v=' + Date.now();
      script.onload = () => {
        console.log('Unity game.loader.js loaded successfully');
        
        // Initialize Unity with the newer format
        if (window.createUnityInstance) {
          try {
            console.log('Initializing Unity with game configuration...');
            
            // Use uncompressed files for local development, compressed for production
            const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
            const fileExtension = isLocalDev ? '' : '.gz';
            
            const config = {
              dataUrl: `/game/Build/game.data${fileExtension}?v=` + Date.now(),
              frameworkUrl: `/game/Build/game.framework.js${fileExtension}?v=` + Date.now(),
              codeUrl: `/game/Build/game.wasm${fileExtension}?v=` + Date.now(),
              streamingAssetsUrl: '/game/StreamingAssets',
              companyName: 'DefaultCompany',
              productName: 'SnakeioClient',
              productVersion: '1.0',
              showBanner: (msg, type) => {
                console.log('Unity Banner:', msg, type);
                if (type === 'error') {
                  setError('Unity Error: ' + msg);
                  setIsLoading(false);
                }
              },
            };
            
            window.createUnityInstance(
              document.getElementById('unity-canvas'),
              config,
              (progress) => {
                console.log('Unity loading progress:', progress);
                const loadingBarFill = document.getElementById('unity-loading-bar-fill');
                if (loadingBarFill) {
                  loadingBarFill.style.width = (progress * 100) + '%';
                }
              }
            ).then((unityInstance) => {
              console.log('Unity WebGL loaded successfully!');
              setIsLoading(false);
              isUnityLoading = false; // Reset global loading flag
              
              const loadingBar = document.getElementById('unity-loading-bar');
              if (loadingBar) {
                loadingBar.style.display = 'none';
              }
              
              // Store the unity instance references
              unityInstanceRef.current = unityInstance;
              window.unityInstance = unityInstance;
              
              // Apply proper sizing immediately after Unity loads
              setTimeout(() => {
                console.log('Applying initial canvas sizing after Unity load...');
                // Let Unity handle its own scaling completely
                const canvas = document.getElementById('unity-canvas');
                if (canvas) {
                  // Don't set canvas.width/height - let Unity handle it
                  // Just set CSS limits to prevent performance issues
                  const maxWidth = 1700;
                  const maxHeight = 956;
                  
                  canvas.style.maxWidth = `${maxWidth}px`;
                  canvas.style.maxHeight = `${maxHeight}px`;
                  canvas.style.width = '100%';
                  canvas.style.height = '100%';
                  
                  console.log(`Canvas limits set to: ${maxWidth}x${maxHeight}, Unity handles actual size`);
                }
              }, 100);
              
              // Apply performance optimizations to canvas
              const canvas = document.getElementById('unity-canvas');
              if (canvas) {
                if (performanceMode) {
                  canvas.classList.add('performance-mode');
                  console.log('Performance mode enabled for large screen');
                }
                
                  // Let Unity handle its own scaling, just set reasonable limits
                  const maxWidth = 1700;
                  const maxHeight = 956;
                  
                  canvas.style.maxWidth = `${maxWidth}px`;
                  canvas.style.maxHeight = `${maxHeight}px`;
                  canvas.style.width = '100%';
                  canvas.style.height = '100%';
                  
                  console.log(`Canvas limits set to: ${maxWidth}x${maxHeight}, letting Unity handle scaling`);
              }
              
              // Add event listener for Unity console messages
              window.addEventListener('message', (event) => {
                if (event.data && event.data.type === 'unity-console') {
                  console.log('Unity Console:', event.data.message);
                }
              });
            }).catch((error) => {
              console.error('Unity WebGL Error:', error);
              isUnityLoading = false; // Reset global loading flag
              
              // Check for compression-related errors
              if (error && (error.toString().includes('br') || error.toString().includes('Brotli'))) {
                setCompressionError(true);
                setError('Compression Error: Brotli compression not supported. Please rebuild Unity with compression disabled or use HTTPS.');
              } else {
                setError('Unity WebGL failed to load: ' + error);
              }
              setIsLoading(false);
            });
          } catch (err) {
            console.error('Unity instantiation error:', err);
            isUnityLoading = false; // Reset global loading flag
            setError('Failed to instantiate Unity: ' + err.message);
            setIsLoading(false);
          }
        } else {
          isUnityLoading = false; // Reset global loading flag
          setError('Unity WebGL loader not found');
          setIsLoading(false);
        }
      };
      script.onerror = () => {
        console.error('Failed to load game.loader.js');
        isUnityLoading = false; // Reset global loading flag
        setError('Failed to load Unity WebGL loader script. Check if build files are in the correct location.');
        setIsLoading(false);
      };
      document.head.appendChild(script);
    };
    socketIOScript.onerror = () => {
      console.error('Failed to load Socket.IO client library');
      isUnityLoading = false; // Reset global loading flag
      setError('Failed to load Socket.IO client library. This is required for game networking.');
      setIsLoading(false);
    };
    document.head.appendChild(socketIOScript);
  }, [performanceMode, scaleToFit, scaleUnity]);

  // Handle window resize for responsive scaling
  useEffect(() => {
    const handleResize = () => {
      const { screenSize: newScreenSize, performanceMode: newPerformanceMode, force1920x1080: newForce1920x1080, scaleToFit: newScaleToFit, scaleUnity: newScaleUnity, scaleMobile: newScaleMobile } = detectScreenSizeAndPerformance();
      setScreenSize(newScreenSize);
      setPerformanceMode(newPerformanceMode);
      setForce1920x1080(newForce1920x1080);
      setScaleToFit(newScaleToFit);
      setScaleUnity(newScaleUnity);
      setScaleMobile(newScaleMobile);
      
      console.log(`Resize flags: scaleToFit=${newScaleToFit}, scaleUnity=${newScaleUnity}, scaleMobile=${newScaleMobile}`);
      
      // Update Unity canvas size if Unity is loaded
      if (unityInstanceRef.current) {
        const canvas = document.getElementById('unity-canvas');
        if (canvas) {
          // Apply performance optimizations to canvas
          if (newPerformanceMode) {
            canvas.classList.add('performance-mode');
          } else {
            canvas.classList.remove('performance-mode');
          }
          
          // Let Unity handle its own scaling, just set reasonable limits
          const maxWidth = 1700;
          const maxHeight = 956;
          
          canvas.style.maxWidth = `${maxWidth}px`;
          canvas.style.maxHeight = `${maxHeight}px`;
            canvas.style.width = '100%';
            canvas.style.height = '100%';
          
          console.log(`Canvas limits set to: ${maxWidth}x${maxHeight}, letting Unity handle scaling`);
        }
      }
    };
    
    // Initial detection
    handleResize();
    
    // Add resize listener
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    loadUnityWebGL();
    
    // Cleanup function for component unmount
    return () => {
      isUnmountingRef.current = true;
      // Use setTimeout to ensure React has finished DOM operations before cleanup
      setTimeout(() => {
        cleanupUnityInstance();
      }, 0);
    };
  }, [loadUnityWebGL, cleanupUnityInstance]);



  const retryLoad = () => {
    // Clean up existing Unity instance and reload
    cleanupUnityInstance();
    
    // Use setTimeout to ensure React has finished DOM operations
    setTimeout(() => {
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }
    loadUnityWebGL();
    }, 0);
  };

  const showCompressionHelp = () => {
    const helpText = `
Unity WebGL Build Issues:

1. **HTTPS Required**: Unity WebGL uses Brotli compression which requires HTTPS
2. **Rebuild Options**:
   - Rebuild Unity with compression disabled
   - Use HTTPS in development
   - Use localhost (which allows compression)

3. **Quick Fix**: Rebuild Unity WebGL with these settings:
   - Compression Format: Disabled
   - Development Build: Checked
   - WebGL Memory: 512MB
   - WebGL Template: Default

4. **Alternative**: Use a local HTTPS server for development
    `;
    alert(helpText);
  };

  return (
    <div 
      className={`webgl-container ${screenSize}-screen ${performanceMode ? 'performance-mode' : ''} ${force1920x1080 ? 'force-1920x1080' : ''} ${scaleToFit ? 'scale-to-fit' : ''} ${scaleUnity ? 'scale-unity' : ''}`} 
      ref={containerRef}
    >
      {isLoading && !error && (
        <div className="loading-overlay">
          <img src="/LoadingScreen.webp" alt="Loading Screen" className="loading-image" />
          <div className="loading-indicator">
            <div className="loading-spinner"></div>
            <p>Loading BarrelRunner.io...</p>
          </div>
        </div>
      )}
      
      {error && (
        <div className="error-overlay">
          <div className="error-content">
            <h3>Game Loading Error</h3>
            <p>{error}</p>
            
            {compressionError && (
              <div className="compression-help">
                <p><strong>This is likely a compression issue.</strong></p>
                <button onClick={showCompressionHelp} className="help-button">
                  Show Help
                </button>
              </div>
            )}
            
            <div className="error-actions">
              <button onClick={retryLoad} className="retry-button">
                Retry
              </button>
              {compressionError && (
                <button onClick={() => window.location.href = '/game'} className="back-button">
                  Back to Menu
                </button>
              )}
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default WebGLContainer; 